import React from "react";
import { Server, Code, Database, Laptop } from "lucide-react";
import { API_URLS, ENVIRONMENT_LABEL, nextEnvironment, type ApiEnvironment } from "../../config/api";
import { useApi } from "../../context/ApiContext";
import { expireSession, getAccessToken } from "../../config/authFetch";

const ENV_COLOR: Record<ApiEnvironment, string> = {
  development: "bg-orange-500 hover:bg-orange-600",
  production: "bg-green-500 hover:bg-green-600",
  local: "bg-sky-600 hover:bg-sky-700",
};

const ApiToggle: React.FC = () => {
  const { environment, isRefreshing, switchEnvironment } = useApi();

  const handleToggle = () => {
    const newEnvironment = nextEnvironment(environment);

    switchEnvironment(newEnvironment);

    // No session for this environment yet — go log in to it.
    if (!getAccessToken(newEnvironment)) {
      expireSession(newEnvironment);
      return;
    }

    // Show success message
    console.log(
      `Switched to ${newEnvironment} environment: ${API_URLS[newEnvironment]}`
    );
  };

  const getEnvironmentColor = () => ENV_COLOR[environment];

  const getEnvironmentIcon = () => {
    if (environment === "local") return <Laptop className="w-4 h-4" />;
    return environment === "development" ? (
      <Code className="w-4 h-4" />
    ) : (
      <Database className="w-4 h-4" />
    );
  };

  const getEnvironmentText = () => ENVIRONMENT_LABEL[environment].short;

  return (
    <div className="flex items-center gap-3">
      <div className="text-sm text-gray-600">
        <span className="font-medium">API:</span>
        <span className="ml-1 text-xs bg-gray-100 px-2 py-1 rounded">
          {API_URLS[environment].replace("https://", "").replace("http://", "")}
        </span>
      </div>

      <button
        onClick={handleToggle}
        disabled={isRefreshing}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg font-semibold text-white transition-all
          ${getEnvironmentColor()}
          ${isRefreshing ? "opacity-50 cursor-not-allowed" : "hover:shadow-md"}
        `}
        title={`Currently using ${environment} API. Click to switch to ${nextEnvironment(environment)}.`}
      >
        <Server className="w-4 h-4" />
        {isRefreshing ? (
          <span className="animate-pulse">Switching...</span>
        ) : (
          <>
            {getEnvironmentIcon()}
            <span>{getEnvironmentText()}</span>
          </>
        )}
      </button>
    </div>
  );
};

export default ApiToggle;
